import React, { useMemo, useCallback } from "react";
import { motion } from "motion/react";
import { IAny } from "@/types/breeze/Any";
import useKanbanStore, {
  KanbanCard as IKanbanCard,
  KanbanColumn,
} from "./store";
import { formatDate, isDateField } from "@/utils/date";
import { ImageIcon } from "lucide-react";
import { ImageFile } from "@/components/carousel/ImageCarousel";
import ImageCarousel from "@/components/carousel/ImageCarousel";
import { useAuth } from "@/providers/AuthProvider";

// Define a more specific type for our Kanban card
interface TypedKanbanCard extends Omit<IKanbanCard, "rawData" | "entityName"> {
  rawData?: IAny & {
    id: number;
    locked: boolean;
    entityName?: string;
    created_at?: string;
    updated_at?: string;
    // TODO: fix typing here
    [key: string]: any;
  };
  images?: ImageFile[];
  title: string;
  description?: string;
  column_id: string;
  position: number;
  entityName?: string;
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown; // Allow dynamic fields
}

// Define a type for column data
interface ColumnData extends KanbanColumn {
  id: string;
  title: string;
  position: number;
  sort_order: Record<number, number | string>;
}

// Define a type for entity data with correctly typed columns
interface BoardEntityData {
  items: Record<string | number, IAny>;
  columns: Record<string, Record<string, ColumnData>>;
  currentStatusField?: string;
}

// Fields to hide in the card display
const HIDDEN_FIELDS = [
  "images",
  "tenant_id",
  "tenant id",
  "id",
  "entityName",
  "column_id",
  "position",
  "locked",
  "_customClasses",
  "_directClickHandler",
  "color",
  "EntityName",
  "_customStyles",
  "onClick",
];

// Define our card component
const Card = ({
  card,
  dragging,
}: {
  card: TypedKanbanCard;
  dragging: boolean;
}) => {
  const { currentTenant } = useAuth();
  // Get board value without causing re-renders
  const board = useKanbanStore((state) => state.board);

  // Get the entity name and status field
  const entityName = card.entityName || "";
  const currentStatusField = useMemo(() => {
    if (!entityName) return "";
    return board[entityName]?.currentStatusField || "";
  }, [board, entityName]);

  // Find the most up-to-date card data from the board
  const currentCardData = useMemo(() => {
    if (!board || !entityName || !board[entityName]) return card;

    const entityData = board[entityName] as unknown as BoardEntityData;
    const statusFieldToUse = currentStatusField;

    // Early return if no status field or no columns
    if (!statusFieldToUse || !entityData.columns) return card;

    // Check if the status field exists in the columns
    if (
      typeof statusFieldToUse !== "string" ||
      !entityData.columns[statusFieldToUse]
    ) {
      return card;
    }

    // Now we know statusFieldToUse is a valid key in columns
    const columnsForStatus = entityData.columns[statusFieldToUse] as Record<
      string,
      ColumnData
    >;
    if (!columnsForStatus) return card;

    // Iterate over columns to find the card
    const columnKeys = Object.keys(columnsForStatus);
    for (const columnId of columnKeys) {
      const column = columnsForStatus[columnId] as ColumnData;
      if (!column || !column.sort_order) continue;

      // Check if the card is in this column's sort_order
      const sortOrderEntries = Object.entries(column.sort_order);

      const cardInColumn = sortOrderEntries.some(
        ([, id]) => String(id) === String(card.id),
      );

      if (cardInColumn) {
        // This card is in this column, get the item data
        if (!entityData.items || typeof card.id === "undefined") return card;

        const itemKey =
          typeof card.id === "number"
            ? card.id
            : parseInt(card.id as string, 10);
        if (isNaN(itemKey)) return card;

        const itemData = entityData.items[itemKey] as IAny;
        if (!itemData) return card;

        // Check if the card has moved by comparing column IDs
        const hasStatusChanged = card.column_id !== card.column_id;
        const now = new Date().toISOString();

        // Create a safe update for the status field
        const statusUpdate: Record<string, string> = {};
        if (typeof statusFieldToUse === "string") {
          statusUpdate[statusFieldToUse] = card.column_id;
        }

        // Create new card object with updated data
        const newCard = {
          ...card,
          column_id: card.column_id, // Ensure column_id is updated to match the column
          rawData: {
            ...(card.rawData || {}),
            ...itemData,
            ...statusUpdate, // Use the safely created status update
            // Update timestamp if status has changed
            updated_at: hasStatusChanged
              ? now
              : itemData.updated_at || card.rawData?.updated_at,
            created_at: card.rawData?.created_at || itemData.created_at,
          },
        };

        return newCard;
      }
    }
    return card;
  }, [board, card, entityName, currentStatusField]);

  // Get card data by combining rawData with direct card properties
  const cardData = useMemo(() => {
    const baseData = (currentCardData as TypedKanbanCard).rawData || {
      id: currentCardData.id,
      locked: false,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    const cardFields = Object.entries(currentCardData).reduce(
      (acc: Record<string, unknown>, [key, value]) => {
        if (key !== "rawData" && value !== undefined) {
          acc[key] = value;
        }
        return acc;
      },
      {},
    );

    // Preserve all the original data and timestamps
    const mergedData: TypedKanbanCard = {
      ...baseData,
      ...cardFields,
      id: currentCardData.id,
      title: currentCardData.title || "Untitled",
      column_id: String(currentCardData.column_id || ""),
      position: Number(currentCardData.position || 0),
      entityName:
        (currentCardData.rawData?.entityName as string) ||
        (cardFields.entityName as string),
      created_at:
        (currentCardData.rawData?.created_at as string) ||
        (cardFields.created_at as string),
      updated_at:
        (currentCardData.rawData?.updated_at as string) ||
        (cardFields.updated_at as string),
      rawData: {
        ...baseData,
        id:
          typeof currentCardData.id === "string"
            ? parseInt(currentCardData.id, 10)
            : typeof currentCardData.id === "number"
              ? currentCardData.id
              : 0, // Fallback to 0 if id is neither string nor number
        locked: false,
      },
    };

    // Add the current status field value if it exists and is a string
    if (currentStatusField && typeof currentStatusField === "string") {
      // Type assertion for the index signature
      (mergedData as Record<string, unknown>)[currentStatusField] =
        currentCardData.column_id;
      if (mergedData.rawData) {
        (mergedData.rawData as Record<string, unknown>)[currentStatusField] =
          currentCardData.column_id;
      }
    }

    return mergedData;
  }, [currentCardData, currentStatusField]);

  // Declare handleClick here after cardData is defined
  const handleClick = useCallback(() => {
    if (!dragging && cardData?.id && cardData?.entityName) {
      // Navigate to the card detail page
      window.location.href = `/org/${currentTenant?.id}/entities/${cardData.entityName}/${cardData.id}`;
      return true;
    }
    return false;
  }, [dragging, cardData?.id, cardData?.entityName]);

  // Format dates for display - use the most recent data
  const formattedDates = useMemo(() => {
    const created = cardData.created_at ? new Date(cardData.created_at) : null;
    const updated = cardData.updated_at ? new Date(cardData.updated_at) : null;

    // Only show updated if it's different from created
    const showUpdated =
      updated && created && updated.getTime() !== created.getTime();

    return {
      // Only return created date if we're not showing updated
      created: !showUpdated ? created?.toLocaleString() : null,
      updated: showUpdated ? updated?.toLocaleString() : null,
    };
  }, [cardData.created_at, cardData.updated_at]);

  // Display entries in a logical order
  const cardEntries = useMemo(() => {
    if (!cardData) return [];

    // First gather all fields from both card and rawData
    const allFields = new Map<string, unknown>();

    // Add fields from rawData first (they'll be overridden by direct props if needed)
    if (cardData.rawData && typeof cardData.rawData === "object") {
      Object.entries(cardData.rawData).forEach(([key, value]) => {
        allFields.set(key, value);
      });
    }

    // Add top-level card fields (these take precedence)
    Object.entries(cardData).forEach(([key, value]) => {
      if (key !== "rawData") {
        allFields.set(key, value);
      }
    });

    // Convert back to entries array for filtering
    return Array.from(allFields.entries())
      .filter(([key, value]) => {
        // More specific checks for problematic fields
        const lowercaseKey = key.toLowerCase();

        if (
          // Standard checks
          value === null ||
          value === undefined ||
          HIDDEN_FIELDS.includes(lowercaseKey) ||
          key === "created_at" ||
          key === "updated_at" ||
          key === "title" ||
          key === "description" ||
          typeof value === "object" ||
          // Explicit checks for fields that need to be hidden
          key === "_customClasses" ||
          key === "customClasses" ||
          key === "_directClickHandler" ||
          key === "directClickHandler" ||
          key === "entityName" ||
          key === "EntityName" ||
          key === "color" ||
          key === "Color"
        ) {
          return false;
        }

        return true;
      })
      .sort(([keyA], [keyB]) => {
        if (keyA === currentStatusField) return -1;
        if (keyB === currentStatusField) return 1;

        const priorityFields = [
          "make",
          "model",
          "registration",
          "status",
          "state",
        ];
        const aPriority = priorityFields.some((field) =>
          keyA.toLowerCase().includes(field),
        );
        const bPriority = priorityFields.some((field) =>
          keyB.toLowerCase().includes(field),
        );

        if (aPriority && !bPriority) return -1;
        if (!aPriority && bPriority) return 1;

        return keyA.localeCompare(keyB);
      });
  }, [cardData, currentStatusField]);

  // Extract image files with proper typing
  const imageFiles = useMemo(() => {
    const images = cardData?.rawData?.images;
    if (!images || !Array.isArray(images)) return [];

    const filteredImages = images.filter(
      (file: ImageFile) =>
        file &&
        typeof file === "object" &&
        file.file_type?.startsWith("image/"),
    );

    return filteredImages;
  }, [cardData]);

  // Get the column name from the column ID for display
  const getColumnNameFromId = useCallback(
    (columnId: string) => {
      if (!board || !entityName || !currentStatusField) return columnId;

      const entityData = board[entityName] as unknown as BoardEntityData;
      if (!entityData || !entityData.columns) return columnId;

      // Type check for the status field
      if (
        typeof currentStatusField !== "string" ||
        !entityData.columns[currentStatusField]
      ) {
        return columnId;
      }

      const columnsForStatus = entityData.columns[currentStatusField] as Record<
        string,
        ColumnData
      >;
      if (!columnsForStatus) return columnId;

      const column = columnsForStatus[columnId] as ColumnData;
      return column?.title || columnId;
    },
    [board, entityName, currentStatusField],
  );

  return (
    <>
      <motion.div
        layout
        data-testid={`card-${cardData.id}`}
        data-card-id={cardData.id}
        data-column-id={cardData.column_id}
        data-column-name={getColumnNameFromId(cardData.column_id)}
        className={`w-[300px] mb-3 rounded-lg bg-white dark:bg-gray-900 shadow-sm ${
          dragging ? "opacity-90" : ""
        }`}
        initial={{ opacity: 0, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0 }}
        onClick={handleClick}
        style={{ touchAction: "none" }}
        transition={{
          duration: 0.2,
          ease: "easeOut",
        }}
        drag={true}
        dragSnapToOrigin={false}
        dragElastic={0}
        dragMomentum={false}
        whileDrag={{
          scale: 1.02,
          zIndex: 999,
          boxShadow: "0 8px 20px rgba(0, 0, 0, 0.1)",
        }}
      >
        <div className="p-4 w-full">
          {/* Title */}
          <div className="font-medium text-lg text-left truncate dark:text-white">
            {String(card.title || "Untitled")}
          </div>

          {/* Description */}
          {card.description && (
            <div className="mt-2 text-sm text-left text-gray-600 dark:text-gray-300 truncate">
              {String(card.description)}
            </div>
          )}

          {/* Card properties */}
          <div className="mt-3 space-y-2">
            {cardEntries.map(([key, value]) => {
              // Format the value
              let displayValue = String(value);
              if (typeof value === "string" && isDateField(key)) {
                displayValue = formatDate(value);
              }

              // Format the key for display
              const displayKey = key.replace(/_/g, " ");

              return (
                <div key={key} className="flex justify-between items-start">
                  <span className="text-xs text-gray-500 dark:text-gray-400 text-left capitalize">
                    {displayKey}:
                  </span>
                  <div className="text-sm text-right truncate max-w-[180px]">
                    {displayValue}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Images carousel */}
          {imageFiles.length > 0 && (
            <div
              className="mt-3 mb-1"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                return false;
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                return false;
              }}
            >
              <div className="overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-800">
                <ImageCarousel
                  files={imageFiles}
                  enableGallery={true}
                  showCounter={true}
                  showControls={true}
                />
              </div>
            </div>
          )}

          {/* Images indicator if there are attachments but none are images */}
          {imageFiles.length === 0 &&
            cardData?.images &&
            Array.isArray(cardData.images) &&
            cardData.images.length > 0 && (
              <div className="mt-2 flex items-center gap-1 text-blue-500">
                <ImageIcon size={16} />
                <span className="text-xs">
                  {cardData.images.length} attachment(s)
                </span>
              </div>
            )}
        </div>

        {/* Footer with dates - only show one date */}
        <div className="mt-2 text-xs text-gray-500">
          {formattedDates.updated ? (
            <div className="text-blue-500 mt-3 p-2 bg-gray-50 dark:bg-gray-800 rounded">
              Updated: {formattedDates.updated}
            </div>
          ) : (
            <div className="mt-3 p-2 bg-gray-50 dark:bg-gray-700 rounded">
              Created: {formattedDates.created}
            </div>
          )}
        </div>
      </motion.div>
    </>
  );
};

// Render function for the react-kanban library
export const renderCard = (
  cardData: Record<string, unknown>,
  { dragging }: { dragging: boolean },
) => {
  // Ensure cardData has the expected structure
  const typedCardData: TypedKanbanCard = {
    id:
      typeof cardData.id === "string"
        ? parseInt(cardData.id as string, 10)
        : (cardData.id as number),
    title: (cardData.title as string) || "Untitled",
    description: cardData.description as string | undefined,
    column_id: (cardData.column_id as string) || "",
    position: Number(cardData.position || 0),
    entityName: cardData.entityName as string | undefined,
    created_at: cardData.created_at as string | undefined,
    updated_at: cardData.updated_at as string | undefined,
    rawData: cardData.rawData as IAny | undefined,
    ...cardData, // Include any other properties
  };

  return (
    <Card
      key={`card-${typedCardData.id}`}
      card={typedCardData}
      dragging={dragging}
    />
  );
};

export default Card;
